import { api, withIdempotencyKey, type Page } from './client'

/** F-097: una riga del cestino dei lead. */
export interface LeadTrashItem {
  id: number
  label: string
  deletedAt: string
  deletedBy: number | null
  deleteReason: string | null
}

export interface LeadTrashPage {
  total: number
  page: number
  pageSize: number
  items: LeadTrashItem[]
}

export interface Lead {
  id: number
  /** F-097: quando il lead è stato spostato nel cestino (solo `GET /api/leads/{id}` lo restituisce); assente = lead attivo. */
  deletedAt?: string | null
  deletedBy?: number | null
  deleteReason?: string | null
  firstName: string
  lastName: string
  companyName?: string
  email?: string
  phone?: string
  /**
   * Codice Fiscale — TASK-0149 (REQ-066-001). Optional, like every other contact field here: a lead
   * is a pre-qualification record and usually has at most one of the four identifying fields the
   * backend's duplicate check reads. Present on both `GET /api/leads` (added to
   * `LeadsController.List`'s explicit projection in the same task) and `GET /api/leads/{id}` (which
   * returns the raw entity), so this interface can carry it without a `LeadDetail`-style extension.
   */
  fiscalCode?: string
  /** Partita IVA — TASK-0149 (REQ-066-001). Same shape and same provenance as `fiscalCode` above. */
  vatNumber?: string
  /**
   * External Lead ID — TASK-0198 (REQ-063-001). The identifier an external lead-generation
   * platform (e.g. Meta/Google Lead Ads) assigned to this lead. Free text, no format/vocabulary
   * validation. Same shape and same provenance as `fiscalCode`/`vatNumber` above.
   */
  externalLeadId?: string
  /**
   * Landing Page — TASK-0210, continuation of REQ-063-001 (see also TASK-0198). The URL of the page
   * on which the lead completed its conversion. Free text, no format/vocabulary validation. Same
   * shape and same provenance as `fiscalCode`/`vatNumber`/`externalLeadId` above.
   */
  landingPage?: string
  /**
   * Click ID — TASK-0211, continuation of REQ-063-001 (see also TASK-0198/TASK-0210). The opaque
   * click-tracking token a paid-ads platform (e.g. Google Ads' `gclid`, Meta's `fbclid`) attaches to
   * a conversion. Free text, no format/vocabulary validation. Same shape and same provenance as
   * `fiscalCode`/`vatNumber`/`externalLeadId`/`landingPage` above.
   */
  clickId?: string
  /**
   * Lead Cost — TASK-0212, continuation of REQ-063-001 (see also TASK-0198/TASK-0210/TASK-0211). An
   * optional monetary cost associated with acquiring this lead. Unlike `externalLeadId`/
   * `landingPage`/`clickId` above (all `string`), this is `number` — same shape as `estimatedValue`
   * below (a `decimal?` on the backend), its direct same-entity precedent. Same provenance as
   * `fiscalCode`/`vatNumber`/`externalLeadId`/`landingPage`/`clickId` above.
   */
  cost?: number
  /**
   * UTM Source — TASK-0213, continuation of REQ-063-001 (see also TASK-0198/TASK-0210/TASK-0211/
   * TASK-0212). One of the 5 standard UTM tracking parameters, bundled as ONE task since all 5 are
   * the identical concept with identical shape/provenance — same bundling precedent as
   * `fiscalCode`/`vatNumber` above (TASK-0149). Free text, no format/vocabulary validation. Same
   * shape and same provenance as `fiscalCode`/`vatNumber`/`externalLeadId`/`landingPage`/`clickId`
   * above.
   */
  utmSource?: string
  /** UTM Medium — TASK-0213 (REQ-063-001). Same reasoning as `utmSource` above. */
  utmMedium?: string
  /** UTM Campaign — TASK-0213 (REQ-063-001). Same reasoning as `utmSource` above. */
  utmCampaign?: string
  /** UTM Content — TASK-0213 (REQ-063-001). Same reasoning as `utmSource` above. */
  utmContent?: string
  /** UTM Term — TASK-0213 (REQ-063-001). Same reasoning as `utmSource` above. */
  utmTerm?: string
  /**
   * Sub-campaign/Channel/Partner/Module/Call Center Source/Call Center Operator — TASK-0214,
   * continuation of REQ-063-001 (see also TASK-0198/TASK-0210/TASK-0211/TASK-0212/TASK-0213). The
   * LAST 6 residuals of this requirement's attribution fields, closing it completely (PARTIAL ->
   * DONE). Bundled as ONE task since all 6 are short free-text labels with zero declared
   * vocabulary — same bundling precedent as `utmSource` etc. above. Free text, no format/vocabulary
   * validation. Same shape and same provenance as `fiscalCode`/`vatNumber`/`externalLeadId`/
   * `landingPage`/`clickId`/`utmSource` above.
   */
  subCampaign?: string
  /** Channel — TASK-0214 (REQ-063-001). Same reasoning as `subCampaign` above. */
  channel?: string
  /** Partner — TASK-0214 (REQ-063-001). Same reasoning as `subCampaign` above. */
  partner?: string
  /** Module — TASK-0214 (REQ-063-001). Same reasoning as `subCampaign` above. */
  module?: string
  /** Call Center Source — TASK-0214 (REQ-063-001). Same reasoning as `subCampaign` above. */
  callCenterSource?: string
  /** Call Center Operator — TASK-0214 (REQ-063-001). Same reasoning as `subCampaign` above. */
  callCenterOperator?: string
  /**
   * Call Attempts — TASK-0199, continuation of REQ-116-001 (see also TASK-0149/TASK-0198). A
   * manually-editable counter of the number of contact attempts made on this lead — no
   * auto-increment mechanism exists, an agent edits it by hand. Same shape and provenance as
   * `fiscalCode`/`vatNumber`/`externalLeadId` above (present on both `GET /api/leads` — added to
   * `LeadsController.List`'s projection in the same task, since it is operationally relevant for
   * call-center list-view triage — and `GET /api/leads/{id}`).
   */
  callAttempts?: number
  /**
   * Lead Temperature — TASK-0170 (REQ-088-001). Manually-set classification
   * (`cold`/`warm`/`hot`/`very_hot`), same shape and provenance as `fiscalCode`/`vatNumber` above
   * (present on both `GET /api/leads` — added to `LeadsController.List`'s projection in the same
   * task — and `GET /api/leads/{id}`). Unlike `priority` (never on this interface, only on
   * `LeadCreateRequest`, and always non-empty), `temperature` can legitimately be `undefined`: the
   * backend field has no default, so an unclassified lead simply omits it.
   */
  temperature?: string
  /**
   * TASK-0182 (REQ-106-001). Whether this lead has been contested, and the applied
   * `LeadContestReasons` value it was contested with. Unlike `fiscalCode`/`vatNumber`/`temperature`
   * above, deliberately NOT added to `LeadsController.List`'s explicit projection (out of this
   * task's scope — no list-view consumer needs it yet); present only via `GET /api/leads/{id}`,
   * which returns the raw entity and so serializes both automatically, which is why this interface
   * can carry them without a `LeadDetail`-style extension. `contestReason` is `undefined` until
   * `isContested` is `true` — `Lead.MarkContested` (backend) always sets both together, never one
   * without the other.
   */
  isContested?: boolean
  contestReason?: string
  /** F-120: esito — `Aperta` | `Accettata` | `Respinta` (assente se mai contestato). */
  contestStatus?: 'Aperta' | 'Accettata' | 'Respinta' | null
  contestedAt?: string | null
  contestDecidedAt?: string | null
  contestDecisionNote?: string | null
  /**
   * Raw Source Data — TASK-0234 (REQ-063-002): "System must maintain original data received from
   * source for later verification". The original, unmodified payload received from whatever
   * created this lead (a webhook body, a CSV-import row, or any other future ingestion connector —
   * see REQ-062-001/REQ-010/011, all NOT_STARTED as of this writing), preserved verbatim. Free-form
   * text, NOT guaranteed to be valid JSON (backend stores it as unbounded `text`, not `jsonb` — see
   * `Lead.RawSourceData`'s own remarks) — render as plain text only, never
   * `dangerouslySetInnerHTML`, since this payload is externally supplied. Settable only by the
   * backend's `Create`, never by `UpdateDetails`, so a later edit can never overwrite it; present
   * only via `GET /api/leads/{id}` (which returns the raw entity), same provenance as
   * `isContested`/`contestReason` above.
   */
  rawSourceData?: string
  /**
   * Interest Sector — REQ-066-002. The per-request dimension that lets several leads created
   * against the SAME contact be told apart: one customer can legitimately generate a lead for
   * "Energia", a separate lead for "Fibra" and a third for "Telefonia" — the requirement's own
   * example. Free text, no format/vocabulary validation, same shape and same provenance as
   * `fiscalCode`/`vatNumber`/`source` above — present on both `GET /api/leads` (added to
   * `LeadsController.List`'s explicit projection in the same task, since it is the dimension the
   * inbox list view needs to tell a contact's several leads apart, not merely an attribution
   * detail) and `GET /api/leads/{id}` (which returns the raw entity).
   */
  interestSector?: string
  /** F-110 (REQ-062-003): località del lead, tutte facoltative; provincia = sigla di 2 lettere, CAP = 5 cifre. */
  city?: string | null
  province?: string | null
  postalCode?: string | null
  /** F-116 (REQ-070-002): numero di dipendenti dell'azienda, facoltativo; entra nell'instradamento e nel punteggio (`employees_count`). */
  employeesCount?: number | null
  status: string
  /** F-102: codice dello stato configurabile (api/lead-statuses) che il lead porta sopra il ciclo di vita; assente = nessuno. Nella lista si chiama `statusDetail`, nel dettaglio `statusDetailCode`. */
  statusDetail?: string | null
  statusDetailCode?: string | null
  statusDetailAt?: string | null
  leadType?: string
  source?: string
  assignedAgentId?: number
  /** F-080 (REQ-087-001): Lead Score 0-100; assente/null se nessuna regola di punteggio e' attiva. */
  score?: number | null
  /** F-080: spiegazione del punteggio, JSON `{total, raw, clamped, truncated, rules:[{id,name,points}]}`. Solo su `GET /api/leads/{id}`. */
  scoreExplanationJson?: string | null
  scoredAt?: string | null
  /** F-080: quando il lead e' stato assegnato all'agente attuale. */
  assignedAt?: string | null
  /** F-080: livello di escalation gia' notificato (0 nessuno, 1 agente, 2 responsabile). */
  escalationLevel?: number
  lastEscalatedAt?: string | null
  createdAt: string
}

/**
 * REQ-061-002 (spec section 61): the shape of `GET /api/leads/{id}/journey` — the full
 * reconstructed path for one lead, straight off `LeadJourneyController.GetJourney` (backend). Every
 * timestamp field is an ISO string, same convention as `Lead.createdAt` above. No monetary field is
 * ever present here (`source.cost`/any contract amount) — see the backend controller's own remarks
 * for why: a pure provenance/timeline view needs no `CanSeeRealAmounts` gate.
 */
export interface LeadJourney {
  source: {
    source?: string
    campaignId?: number
    externalLeadId?: string
    landingPage?: string
    clickId?: string
    utmSource?: string
    utmMedium?: string
    utmCampaign?: string
    utmContent?: string
    utmTerm?: string
    subCampaign?: string
    channel?: string
    partner?: string
    module?: string
    callCenterSource?: string
    callCenterOperator?: string
  }
  statusHistory: {
    id: number
    fromStatus?: string
    toStatus: string
    actorUserId?: number
    reason?: string
    note?: string
    origin: string
    occurredAt: string
  }[]
  appointments: {
    id: number
    startAt: string
    status: string
    outcome?: string
    outcomeReason?: string
    contractId?: number
  }[]
  conversion: {
    convertedCustomerId?: number
    convertedContractId?: number
    convertedAt?: string
  }
  /** F-080 (REQ-109-001): tappa "assegnato". */
  assignment?: { assignedAgentId?: number | null; assignedAt?: string | null; protectedUntil?: string | null }
  /** F-080 (REQ-109-001): tappa "escalation" per inattivita'. */
  escalation?: { level: number; lastEscalatedAt?: string | null }
  /** `null` when the lead never converted — there is no contract to have a history at all. */
  contractStatusHistory: {
    id: number
    fromStatus?: string
    toStatus: string
    actorUserId?: number
    reason?: string
    note?: string
    origin: string
    occurredAt: string
  }[] | null
}

export interface LeadFilters {
  search?: string
  status?: string
  /** F-102: filtra per codice dello stato configurabile. */
  statusDetail?: string
  /** F-080: ordinamento per punteggio; assente = ordine di sempre. */
  sort?: 'score_desc' | 'score_asc'
  page?: number
  size?: number
}

/**
 * Body of `POST /api/leads`. A superset of `Partial<Lead>` plus `priority`/`estimatedValue` —
 * present on the create form (`LeadFormPage.tsx`) but not on the list-view `Lead` interface — and
 * `forceCreate` (TASK-0107), which mirrors `CompanyRequest.forceCreate` (`companies.ts`): forces
 * creation past a 409 email-duplicate conflict (`DuplicateResponses.Conflict`, backend
 * TASK-0106). Only meaningful when `mode === 'create'`; `update` keeps taking `Partial<Lead>`.
 *
 * TASK-0149 (REQ-066-001) needed no new member here: `fiscalCode`/`vatNumber` were added to `Lead`
 * itself above, so both this request type (`extends Partial<Lead>`) and `update`'s `Partial<Lead>`
 * pick them up automatically — which is what makes the same two fields reach `POST` (where the
 * backend feeds them to the duplicate check) and `PUT` through one declaration.
 *
 * TASK-0170 (REQ-088-001) needed no new member here either, for the identical reason: `temperature`
 * was added to `Lead` above, so this type and `update`'s `Partial<Lead>` pick it up the same way.
 *
 * TASK-0213 (REQ-063-001) needed no new member here either, same reason: `utmSource`/`utmMedium`/
 * `utmCampaign`/`utmContent`/`utmTerm` were added to `Lead` above, so this type and `update`'s
 * `Partial<Lead>` pick up all 5 the same way.
 *
 * TASK-0214 (REQ-063-001) needed no new member here either, same reason: `subCampaign`/`channel`/
 * `partner`/`module`/`callCenterSource`/`callCenterOperator` were added to `Lead` above, so this
 * type and `update`'s `Partial<Lead>` pick up all 6 the same way — verified by reading this file
 * directly, not assumed.
 *
 * REQ-066-002 needed no new member here either, same reason: `interestSector` was added to `Lead`
 * above, so this type and `update`'s `Partial<Lead>` pick it up the same way.
 */
export interface LeadCreateRequest extends Partial<Lead> {
  priority?: string
  estimatedValue?: number
  forceCreate?: boolean
  /** F-116: campagna del lead alla creazione (collega il lead e alimenta l'instradamento); ignorata in modifica. */
  campaignId?: number
}

/** Body of a successful `POST /api/leads` (`DuplicateResponses.Created`, backend TASK-0106) —
 * always carries `possibleDuplicates` (possibly empty), populated only with phone-matched
 * candidates since an Email match 409s instead unless `forceCreate` was set. Same shape as
 * `CompanyCreateResult` (`companies.ts`). Typed but intentionally unrendered here, matching every
 * other duplicate-protected form in this frontend (see TASK-0107 card). */
export interface LeadCreateResult {
  id: number
  possibleDuplicates: { entityType: string; id: number; matchedField: string }[]
  /** F-080 (REQ-107-001): presente solo se le regole di proprieta' hanno deciso (cliente con proprietario). */
  ownershipDecision?: { ruleId: number | null; action: 'MantieniProprietario' | 'AssegnaNuovo' | 'TeamLeader'; reason: string }
}

/** One entry of the lead-source list (`GET /api/leads/sources`, only the active ones; `?all=true` adds the deactivated): `code` is what `source` must carry. */
export interface LeadSourceOption {
  code: string
  label: string
  channel?: string
  isActive?: boolean
}

export const leadsApi = {
  sources: (includeInactive = false) =>
    api<LeadSourceOption[]>(`/api/leads/sources${includeInactive ? '?all=true' : ''}`),

  list: (f: LeadFilters = {}) => {
    const p = new URLSearchParams()
    if (f.search) p.set('search', f.search)
    if (f.status) p.set('status', f.status)
    if (f.statusDetail) p.set('statusDetail', f.statusDetail)
    if (f.sort) p.set('sort', f.sort)
    p.set('page', String(f.page ?? 1))
    p.set('pageSize', String(f.size ?? 25))
    return api<Page<Lead>>(`/api/leads?${p}`)
  },

  get: (id: number) => api<Lead>(`/api/leads/${id}`),

  /**
   * REQ-061-002: `GET /api/leads/{id}/journey` — the full reconstructed lead path (source
   * attribution, status history, appointments, conversion, and the resulting contract's own status
   * history once converted). Same thin-passthrough shape as `get` above; the ADR-008 404 the backend
   * returns for an invisible/nonexistent lead surfaces here as a rejected promise, same as `get`.
   */
  journey: (id: number) => api<LeadJourney>(`/api/leads/${id}/journey`),

  create: (data: LeadCreateRequest, idempotencyKey?: string) =>
    api<LeadCreateResult>(
      '/api/leads',
      withIdempotencyKey({ method: 'POST', body: JSON.stringify(data) }, idempotencyKey),
    ),

  update: (id: number, data: Partial<Lead>) =>
    api<Lead>(`/api/leads/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  /**
   * `PATCH /api/leads/{id}/status`. `reason`/`note` are optional and forwarded verbatim to
   * `UpdateStatusRequest.Reason`/`.Note` (`LeadsController.UpdateStatus`, unchanged by TASK-0150) —
   * before TASK-0150 this call never sent either, so `Lead.TransitionTo`'s `reason` parameter was
   * always `null` no matter what. TASK-0150 (REQ-069-001) requires a `reason` from
   * `LeadLostReasons`'s 14-value vocabulary specifically when `status === 'lost'`; the backend
   * enforces that (`Lead.cs`), so this call stays a thin passthrough rather than duplicating the
   * vocabulary check here — see `LeadDetailPage.tsx`'s Lost-reason modal, the only caller that now
   * passes `reason`.
   */
  updateStatus: (id: number, status: string, reason?: string, note?: string) =>
    api<void>(`/api/leads/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, reason, note }),
    }),

  /**
   * F-102: `PATCH /api/leads/{id}/status-detail` - imposta (o con `null` toglie) lo stato configurabile del lead SENZA
   * cambiare il ciclo di vita. Il codice deve valere per lo stato corrente (altrimenti 400).
   */
  updateStatusDetail: (id: number, code: string | null) =>
    api<void>(`/api/leads/${id}/status-detail`, {
      method: 'PATCH',
      body: JSON.stringify({ code }),
    }),

  /**
   * F-102: `PATCH /api/leads/{id}/status` con il solo `detailCode`: un esito finale porta il lead al suo ciclo di vita
   * (Perso, con il motivo dello stato) e scrive la riga di storico.
   */
  applyOutcome: (id: number, detailCode: string, note?: string) =>
    api<void>(`/api/leads/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ detailCode, note }),
    }),

  /**
   * TASK-0182 (REQ-106-001): `PATCH /api/leads/{id}/contest`. `reason` is required — forwarded
   * verbatim to `ContestRequest.Reason` (`LeadsController.Contest`), which the backend validates
   * against `LeadContestReasons`'s 6-value applied vocabulary (`Lead.MarkContested`) and rejects
   * with a `400` if it does not match — same thin-passthrough shape as `updateStatus` above, no
   * vocabulary check duplicated here. See `LeadDetailPage.tsx`'s Contest-reason modal, the only
   * caller.
   */
  markContested: (id: number, reason: string) =>
    api<void>(`/api/leads/${id}/contest`, {
      method: 'PATCH',
      body: JSON.stringify({ reason }),
    }),

  /** F-120: `PATCH /api/leads/{id}/contest/decision` — solo Admin; la decisione e' definitiva. */
  decideContest: (id: number, accepted: boolean, note?: string) =>
    api<void>(`/api/leads/${id}/contest/decision`, {
      method: 'PATCH',
      body: JSON.stringify({ accepted, note: note?.trim() ? note.trim() : undefined }),
    }),

  /**
   * TASK-0047 (REQ-061-001): `POST /api/leads/{id}/convert`. `LeadsController.Convert` requires a
   * `ConvertRequest(long CustomerId, long? ContractId)` body — `CustomerId` is non-nullable and
   * `Lead.Convert` never creates a Customer itself, it only records an id the caller must already
   * have (see `customersApi.create`, the real first step `LeadDetailPage.tsx` now runs before this
   * call). `contractId` stays optional/nullable: there is no way to create a Contract from this
   * flow yet, callers pass `null`. The response shape is `Ok(new { lead.Id, lead.Status,
   * lead.ConvertedCustomerId, lead.ConvertedAt })` (see `LeadsController.cs`), camelCased by
   * System.Text.Json to `convertedCustomerId` — NOT `customerId`, which this used to (wrongly) read
   * and which is always `undefined` on this response, the second half of the bug this task fixes.
   */
  convert: (id: number, customerId: number, contractId: number | null = null) =>
    api<{ id: number; status: string; convertedCustomerId: number; convertedAt: string }>(
      `/api/leads/${id}/convert`,
      { method: 'POST', body: JSON.stringify({ customerId, contractId }) },
    ),

  /**
   * F-097 (REQ-287-001): non cancella la riga, sposta il lead nel cestino (solo Admin). `reason` è facoltativo,
   * al massimo 500 caratteri.
   */
  delete: (id: number, reason?: string) =>
    api<void>(`/api/leads/${id}${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`, { method: 'DELETE' }),

  /** F-097: toglie un lead dal cestino (solo Admin; 409 se non è nel cestino). */
  restore: (id: number) =>
    api<void>(`/api/leads/${id}/restore`, { method: 'POST', body: '{}' }),

  /** F-097: il cestino dei lead (solo Admin), stesso contratto degli altri cestini. */
  trash: (p?: { search?: string; page?: number; pageSize?: number }) => {
    const q = new URLSearchParams()
    if (p?.search) q.set('search', p.search)
    q.set('page', String(p?.page ?? 1))
    q.set('pageSize', String(p?.pageSize ?? 25))
    return api<LeadTrashPage>(`/api/leads/trash?${q}`)
  },

  /**
   * TASK-0278 (F-025): GET call attempts for a lead, ordered by attemptNumber.
   * Returns the full list including attempts that used outcomes since deactivated.
   */
  getCallAttempts: (leadId: number) =>
    api<LeadCallAttempt[]>(`/api/leads/${leadId}/call-attempts`),

  /**
   * TASK-0278 (F-025): POST a new call attempt. The operatorUserId is set server-side
   * from the current user. AttemptNumber is assigned by the server.
   * Errors: 400 if outcomeId doesn't exist or is inactive, 409 if concurrent write collision.
   */
  createCallAttempt: (leadId: number, req: CreateCallAttemptRequest) =>
    api<{ id: number; leadId: number; attemptNumber: number; occurredAt: string }>(
      `/api/leads/${leadId}/call-attempts`,
      { method: 'POST', body: JSON.stringify(req) },
    ),
}

export interface LeadCallAttempt {
  id: number
  leadId: number
  operatorUserId?: number
  outcomeId: number
  phoneNumberCalled?: string
  attemptNumber: number
  occurredAt: string
  notes?: string
  callbackScheduledAt?: string
  /** F-045: with a reason the recontact is a recycling request (lost lead reopened at the date). */
  callbackReason?: string | null
}

export interface CreateCallAttemptRequest {
  outcomeId: number
  phoneNumberCalled?: string
  notes?: string
  callbackScheduledAt?: string
  /** F-045: max 200 chars; requires callbackScheduledAt; with it the date must be in the future. */
  callbackReason?: string
}

/** F-045: max length of callbackReason, mirrors LeadRecontactPolicy.ReasonMaxLength. */
export const RECONTACT_REASON_MAX = 200

/** F-045: one row of GET /api/leads/recontacts (RecontactRow). */
export interface LeadRecontact {
  leadId: number
  leadName: string
  status: string
  assignedAgentId?: number | null
  pipelineId?: number | null
  stageId?: number | null
  recontactAt: string
  reason?: string | null
  callAttemptId: number
}

/** The recontacts endpoint pages with `pageSize` (not `size` as the shared Page<T> type). */
export interface RecontactsPage {
  total: number
  page: number
  pageSize: number
  items: LeadRecontact[]
}

export const recontactsApi = {
  list: (f: { status?: string; from?: string; to?: string; page?: number; pageSize?: number } = {}) => {
    const p = new URLSearchParams()
    if (f.status) p.set('status', f.status)
    if (f.from) p.set('from', f.from)
    if (f.to) p.set('to', f.to)
    p.set('page', String(f.page ?? 1))
    p.set('pageSize', String(f.pageSize ?? 25))
    return api<RecontactsPage>(`/api/leads/recontacts?${p}`)
  },
}

/**
 * TASK-0278 (F-025): call outcome configuration — the vocabulary of possible call results.
 * Reads are open to any authenticated user; writes (create/update) are Admin-only.
 */
export interface CallOutcome {
  id: number
  code: string
  label: string
  isContactUseful: boolean
  closesSequence: boolean
  sortOrder: number
  isActive: boolean
}

export interface CreateCallOutcomeRequest {
  code: string
  label: string
  isContactUseful: boolean
  closesSequence: boolean
  sortOrder: number
  isActive?: boolean
}

export const callOutcomesApi = {
  /**
   * Get all call outcomes, optionally filtered to active only.
   */
  list: (activeOnly = false) =>
    api<CallOutcome[]>(`/api/call-outcomes?activeOnly=${activeOnly}`),

  get: (id: number) =>
    api<CallOutcome>(`/api/call-outcomes/${id}`),

  /**
   * Admin only: create a new call outcome.
   */
  create: (req: CreateCallOutcomeRequest) =>
    api<{ id: number }>('/api/call-outcomes', {
      method: 'POST',
      body: JSON.stringify(req),
    }),

  /**
   * Admin only: update an existing call outcome. Sends all fields.
   */
  update: (id: number, req: CreateCallOutcomeRequest) =>
    api<void>(`/api/call-outcomes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(req),
    }),
}
